import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DatePicker, ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import esES from 'antd/locale/es_ES';
import enUS from 'antd/locale/en_US';
import itIT from 'antd/locale/it_IT';
import { colors } from '../../../themes/typography';

/**
 * InlineDatePicker component - Inline date selection for alert fix planning
 * Phase 4: Clean, minimal date picker that appears within the card
 */
const InlineDatePicker = ({ 
  selectedDate, 
  onChange, 
  onSubmit, 
  onCancel,
  loading = false,
  t 
}) => {
  const { i18n } = useTranslation();
  const locale = { es: esES, en: enUS, it: itIT }[i18n.language] || esES;

  return (
    <Box sx={{
      p: '12px',
      backgroundColor: 'rgba(249, 250, 251, 0.8)', // Very subtle background
      borderRadius: '6px',
      border: '1px solid rgba(107, 114, 128, 0.2)',
      mt: '8px'
    }}>
      {/* Question prompt */}
      <Typography sx={{
        fontSize: '10px',
        color: colors.text.primary,
        fontWeight: 500,
        textAlign: 'center',
        mb: '8px',
        lineHeight: 1.2
      }}>
        {t ? t('whenWillYouFixIt') : 'When do you plan to fix it?'}
      </Typography>

      {/* Date picker */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        mb: '8px'
      }}>
        <ConfigProvider locale={locale}>
          <DatePicker
            value={selectedDate}
            onChange={onChange}
            size="small"
            format={i18n.language === 'es' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
            placeholder={t ? t('selectDate') : 'Select date'}
            disabled={loading}
            style={{
              borderColor: 'rgba(107, 114, 128, 0.3)',
              borderRadius: '4px',
              fontSize: '10px'
            }}
          />
        </ConfigProvider>
      </Box>

      {/* Action buttons */}
      <Box sx={{
        display: 'flex',
        gap: '6px',
        justifyContent: 'center'
      }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{
            minWidth: 'auto',
            px: '8px',
            py: '2px',
            fontSize: '10px', // Match experiment card
            fontWeight: 500,
            color: '#8E8E93', // Apple gray
            backgroundColor: 'transparent',
            border: '1px solid rgba(142, 142, 147, 0.2)',
            borderRadius: '6px', // More rounded
            textTransform: 'none',
            transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            '&:hover': {
              backgroundColor: 'rgba(142, 142, 147, 0.08)',
              borderColor: 'rgba(142, 142, 147, 0.3)'
            },
            '&:disabled': {
              opacity: 0.4
            }
          }}
        >
          {t ? t('cancel') : 'Cancel'}
        </Button>

        <Button
          onClick={onSubmit}
          disabled={!selectedDate || loading}
          sx={{
            minWidth: 'auto',
            px: '12px',
            py: '2px',
            fontSize: '10px', // Match experiment card font size
            fontWeight: 500, // Match experiment card font weight
            color: 'white',
            backgroundColor: '#007AFF', // Apple blue
            border: '1px solid #007AFF',
            borderRadius: '6px', // More rounded
            textTransform: 'none',
            transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            '&:hover': {
              backgroundColor: '#0051D0',
              borderColor: '#0051D0',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0, 122, 255, 0.25)'
            },
            '&:disabled': {
              opacity: 0.4,
              backgroundColor: '#007AFF',
              transform: 'none',
              boxShadow: 'none'
            }
          }}
        >
          {loading ? 
            (t ? t('planning') : 'Planning...') : 
            (t ? t('planRepair') : 'Plan Repair')
          }
        </Button>
      </Box>
    </Box>
  );
};

export default InlineDatePicker;